import React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ReactComponent as ClockIcon } from "../../../inlineSvgs/clock.svg";
import { ReactComponent as LogoIcon } from "../../../inlineSvgs/logo-red.svg";
import { ReactComponent as VisaIcon } from "../../../inlineSvgs/visa.svg";
import SelectMaster from "./SelectMaster";
import SelectService from "./SelectService";
import SelectDate from "./SelectDate";
import { changeSelectedPaymentType, clearOrder } from "../../../store/slice/orderSlice";
import { convertPrice, showNotification } from "../../../config";
import { createOrder, payForOrder } from "../../../api";

const BookingSidebar = () => {
  const { selectedStudio, selectedMaster, selectedService, selectedDate, selectedPaymentType, selectedTime } =
    useSelector((state) => state.orderReducer);

  const dispatch = useDispatch();
  const redirect = useNavigate();
  const isDisabled = !(selectedStudio && selectedMaster && selectedService && selectedTime && selectedPaymentType);
  const priseByRank =
    selectedService && selectedService.priceList.find((price) => price?.rank === selectedMaster?.rank)?.price;

  const selectPaymentType = (type) => {
    return () => dispatch(changeSelectedPaymentType(type));
  };

  const sendOrder = async () => {
    const tzoffset = (new Date()).getTimezoneOffset() * 60000;
    const timeISO = new Date(selectedDate - tzoffset).toISOString();

    const order = {
      paymentType: selectedPaymentType,
      masterId: selectedMaster?.id,
      studioId: selectedStudio?.id,
      from: selectedTime
        ? timeISO.slice(0, -13) +
          (selectedTime.hours < 10 ? `0${selectedTime.hours}` : selectedTime.hours) +
          ":" +
          (selectedTime.minutes != 0 ? selectedTime.minutes : "00")
        : timeISO.slice(0, -5),
      services: [{ serviceId: selectedService?.id, rank: selectedMaster?.rank }],
    };

    try {
      const orderId = await createOrder(order).then(res => res.data.id);

      if (selectedPaymentType === "online") {
        const payUrl = await payForOrder(orderId).then(res => res.data);

        if (typeof payUrl === "string") {
          window.location.href = payUrl;
        } else {
          redirect("/booking/success");
        }
      } else {
        redirect("/booking/success");
      }
    } catch {
      showNotification("Something went wrong, please try again later.");
    }
  };

  return (
    <div className="sidebar">
      <div className="top">
        <Studio studio={selectedStudio} />
        <div className="order-params">
          <SelectService service={selectedService} />
          <SelectMaster master={selectedMaster} />
          <SelectDate date={selectedDate} time={selectedTime} service={selectedService} />
          <div className="payment">
            <div className="payment__title">Payment Method</div>
            <div
              className={selectedPaymentType === "cash" ? "payment__type-item active" : "payment__type-item"}
              onClick={selectPaymentType("cash")}
            >
              <div className="logo">
                <LogoIcon />
              </div>
              <div className="description">
                <div className="title">Pay at the Studio</div>
                <div className="sub-title">Cash or Card</div>
              </div>
              <div className="radio-point"></div>
            </div>
            <div
              className={selectedPaymentType === "online" ? "payment__type-item active" : "payment__type-item"}
              onClick={selectPaymentType("online")}
            >
              <div className="logo">
                <VisaIcon />
              </div>
              <div className="description">
                <div className="title">Online payment</div>
                <div className="sub-title">MasterCard or Visa</div>
              </div>
              <div className="radio-point"></div>
            </div>
          </div>
        </div>
      </div>
      <div className="bottom">
        {selectedService && (
          <div className="price-list">
            <span className="price-list__name">{selectedService.title}</span>
            <span className="price-list__cost">{priseByRank ? convertPrice(priseByRank) : "--- "} €</span>
          </div>
        )}
        <div className="price-list total">
          <span className="total__name">Amount Payable</span>
          <span className="total__cost">{priseByRank ? convertPrice(priseByRank) : "--- "} €</span>
        </div>
        <button onClick={() => sendOrder()} className="beauty-btn fill" disabled={isDisabled}>
          Checkout Now
        </button>
      </div>
    </div>
  );
};

const Studio = ({ studio }) => {
  if (!studio) return null;
  const { title, address, images, schedule } = studio;
  const bgImage = images.length ? images[0] : "./img/BG.jpg";

  const getWorkingTime = (schedule, day = 0) => {
    const workingTimes = schedule?.workingTimes;
    const dayTime = workingTimes && workingTimes[0]?.daysPeriods?.[day];

    if (dayTime) {
      const stringTime =
        dayTime.from.hours + ":" + dayTime.from.minutes + " - " + dayTime.to.hours + ":" + dayTime.to.minutes;
      return stringTime;
    }
  };

  const workingTime = getWorkingTime(schedule);

  const cutText = (text) => {
    if (text?.length > 35) {
      return text.slice(0, 30) + "...";
    }

    return text;
  };

  return (
    <div className="sidebar__studio">
      <div className="studio__img">
        <img src={bgImage} alt="salon" />
      </div>
      <div className="studio__info">
        <p className="title">{title}</p>
        <p className="address">{cutText(address)}</p>
        <p className="work-time">
          <ClockIcon />
          <strong>Work schedule:</strong>
          <span>{workingTime}</span>
        </p>
      </div>
    </div>
  );
};

export default BookingSidebar;
